/**
 * The pipeline, with the lid off.
 *
 * Free, 2026-07-27: "Lets get the UI up and running and expose all the prompts
 * for me to play around with", and then the sharper version of what he wanted
 * to see: "Whatever is being fed into the llm at any moment is what I want to
 * see on my screen then their response", and "if the llm is fed
 * intro+think+q1+think then expected to respond I want to see that series."
 *
 * So the centre column is not the debate transcript. It is the GENERATION LOG:
 * one card per call to the model, showing the exact series of messages that
 * went in and the text that came out. The debate is a side effect of that list.
 *
 * WHY THIS IS ITS OWN SURFACE rather than a mode inside MindPage. MindPage is
 * the thing a visitor meets, and its job is to make the lattice feel like a
 * mind. This is a workbench: it shows the seams on purpose, and the two goals
 * fight each other inside one component.
 *
 * WHAT IT REFUSES TO GUESS. The prompt shown under "exact" comes back from the
 * worker, decoded off `input_ids`. Nothing here reassembles a chat template,
 * because the first time that was measured it turned out Gemma has no system
 * role at all and folds the system message into the first user turn. A
 * reconstruction would have shown a system turn that does not exist.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MindClient, type GenerationRecord } from "../llm/client";
import { DEFAULT_MODEL_KEY, SHIPPED_MODELS } from "../llm/catalog";
import { EmbedClient } from "../lattice/embedClient";
import { loadPack, type LoadedPack } from "../lattice/packStore";
import { PACKS, packByKey } from "../lattice/packs";
import { LoadingOverlay, type LoadingState } from "../LoadingOverlay";
import { cacheUsage, cachedRepos, clearWeightCache, type CacheUsage } from "../lattice/weightCache";
import { formatBytes, probeGpu, type GpuReport } from "../gpu";
import { DEFAULT_CONFIG, type ConverseConfig } from "../converse";
import { RELEVANCE_FLOOR } from "../lattice/aperture";
import {
  proposeAgenda,
  runStagedDebate,
  type AgendaResult,
  type QuestionRound,
  type Speaker,
} from "../debate";
import {
  specsForMode,
  setActivePrompts,
  changedIds,
  missingVars,
  unknownVars,
  type PromptOverrides,
  type PromptId,
  type DebateMode,
} from "../pure/promptKit";

const LS_KEY = "mind.lab.prompts.v1";
const LS_VOICE = "mind.lab.voice.v1";

/** B running the same weights as A. Kept as a value so the picker can say it. */
export const SAME_AS_A = "";

/**
 * A speaker's identity, as edited here rather than as baked into the .rlm.
 *
 * Empty string means "use the pack", NOT "speak with no preamble". The
 * distinction matters: an operator who clears the box is reverting, and a
 * literal empty system prompt would be a different and much worse experiment
 * that they did not ask for.
 */
type Voice = { persona: string; preamble: string };
const BLANK: Voice = { persona: "", preamble: "" };

function loadVoices(): Record<string, Voice> {
  try {
    const raw = localStorage.getItem(LS_VOICE);
    const parsed = raw ? (JSON.parse(raw) as Record<string, Voice>) : {};
    return typeof parsed === "object" && parsed ? parsed : {};
  } catch {
    return {};
  }
}

/** Load edits from the last session. A tuning surface that forgets is useless. */
function loadOverrides(): PromptOverrides {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as PromptOverrides;
    return typeof parsed === "object" && parsed ? parsed : {};
  } catch {
    return {};
  }
}

const short = (p: string) => p.replace(/^The Mind's /, "");

export function DebateLab({ onExit }: { onExit?: () => void }) {
  const [modelKey, setModelKey] = useState(DEFAULT_MODEL_KEY);
  /**
   * A second set of weights for side B.
   *
   * The bay's thesis is one interpreter and swappable memory, and this is how
   * that stops being an assumption. With one model every difference between the
   * two sides is the lattice BY CONSTRUCTION, so the claim cannot fail; give B
   * its own weights and it becomes a measurement with a control arm.
   *
   * Defaults to SAME_AS_A so nothing changes until it is asked for, and because
   * two models means two downloads and two shader compilations.
   */
  const [modelKeyB, setModelKeyB] = useState(SAME_AS_A);
  const [aKey, setAKey] = useState("gardening");
  const [bKey, setBKey] = useState("hermes-trismegistus");
  const [topic, setTopic] = useState(
    "whether plants communicate with each other and whether companion planting works",
  );
  /**
   * Debate or collaborate. Free's idea, and it suits these lattices better:
   * debate needs two memories with purchase on one subject and no two packs
   * overlap, while collaboration needs memories that complement, which is the
   * one thing this catalogue has plenty of.
   */
  const [mode, setMode] = useState<DebateMode>("debate");
  const [maxExchanges, setMaxExchanges] = useState(4);
  const [stagingRetrieval, setStagingRetrieval] = useState(false);
  const [cfg, setCfg] = useState<ConverseConfig>(DEFAULT_CONFIG);

  const [status, setStatus] = useState("idle");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [agenda, setAgenda] = useState<AgendaResult | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [rounds, setRounds] = useState<QuestionRound[]>([]);

  /** Every generation, newest last. This is the point of the page. */
  const [log, setLog] = useState<GenerationRecord[]>([]);
  const [openIds, setOpenIds] = useState<Set<number>>(new Set());
  const [showExact, setShowExact] = useState<Set<number>>(new Set());

  /**
   * The floating loading indicator, same component the visitor page uses.
   *
   * The waits are identical here: hundreds of megabytes, then twenty or thirty
   * seconds of shader compilation. A header that says "loading embedder" with
   * no bytes and no bar reads as a hang, and the lab is where someone sits
   * through those waits most often.
   */
  const [load, setLoad] = useState<LoadingState>({ phase: null, title: "", pct: 0 });
  const [usage, setUsage] = useState<CacheUsage | null>(null);
  const [repos, setRepos] = useState<Record<string, { files: number; bytes: number }>>({});
  const [gpu, setGpu] = useState<GpuReport | null>(null);

  const [overrides, setOverrides] = useState<PromptOverrides>(loadOverrides);
  const [panelOpen, setPanelOpen] = useState(false);
  /** Persona and preamble edits, keyed by pack. Survive a reload like prompts do. */
  const [voices, setVoices] = useState<Record<string, Voice>>(loadVoices);

  const llm = useRef<MindClient | null>(null);
  const llmB = useRef<MindClient | null>(null);
  /**
   * Which weights each client actually holds.
   *
   * `ensureReady` used to guard on `if (!llm.current)`, so the model select was
   * disabled once anything had loaded and switching required a page reload. The
   * loaded key is the honest guard: change the picker and the next run reloads.
   */
  const loadedKey = useRef<string | null>(null);
  const loadedKeyB = useRef<string | null>(null);
  const embedder = useRef<EmbedClient | null>(null);
  const packs = useRef<Record<string, LoadedPack>>({});
  const stop = useRef({ stopped: false });
  const logRef = useRef<HTMLDivElement | null>(null);

  // Edits take effect on the NEXT generation, with no reload and no restart.
  // That is the whole point of the panel: change a line, run again, read the
  // difference in the log directly above.
  useEffect(() => {
    setActivePrompts(overrides);
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(overrides));
    } catch {
      /* private mode, or quota. The edits still apply to this session. */
    }
  }, [overrides]);

  // Voices are read at speaker construction rather than pushed anywhere, so
  // there is no active set to keep in sync. Only persistence to do.
  useEffect(() => {
    try {
      localStorage.setItem(LS_VOICE, JSON.stringify(voices));
    } catch {
      /* same as above */
    }
  }, [voices]);

  /** What is on this device already. Refreshed after anything that downloads. */
  const refreshResources = useCallback(() => {
    void cacheUsage().then(setUsage);
    void cachedRepos().then(setRepos);
  }, []);

  useEffect(() => {
    void probeGpu().then(setGpu);
    refreshResources();
    return () => {
      llm.current?.dispose();
      llmB.current?.dispose();
      embedder.current?.dispose();
    };
  }, [refreshResources]);

  const scrollLog = () => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  /**
   * Records arrive twice, once on send and once on completion, keyed by id.
   * The first is what puts the input on screen WHILE the model works on it,
   * which is the moment Free asked to see and the one a completed-only log
   * would miss entirely.
   */
  const absorb = useCallback((rec: GenerationRecord) => {
    setLog((prev) => {
      const i = prev.findIndex((r) => r.id === rec.id);
      if (i === -1) return [...prev, rec];
      const next = prev.slice();
      next[i] = rec;
      return next;
    });
    requestAnimationFrame(scrollLog);
  }, []);

  /**
   * Get a client holding `key`, reloading if it is holding something else.
   *
   * Replaces the client rather than reloading in place, so the old weights go
   * with the old worker. A `load` on a live worker would leave both sets
   * resident, which on a page that can now hold two models at once is the
   * difference between comfortable and out of memory.
   */
  const loadInto = useCallback(
    async (
      ref: React.MutableRefObject<MindClient | null>,
      loaded: React.MutableRefObject<string | null>,
      key: string,
      side: string,
    ) => {
      if (ref.current && loaded.current === key) return ref.current;
      ref.current?.dispose();
      loaded.current = null;
      const entry = SHIPPED_MODELS.find((m) => m.key === key);
      const title = `${entry?.label ?? key}${side}`;
      const client = new MindClient();
      client.onGeneration(absorb);
      ref.current = client;
      setStatus(`loading ${title}`);
      // Bytes while fetching, then an honest indeterminate bar: there is no
      // real percentage for shader compilation and a fake one is worse than
      // a moving stripe.
      let sawBytes = false;
      setLoad({ phase: "fetch", title, pct: 0 });
      await client.load(key, {
        onProgress: (file, l, total, pct) => {
          sawBytes = true;
          setLoad({ phase: "fetch", title, file, loaded: l, total, pct });
        },
        onMessage: (m) => {
          setStatus(m);
          if (sawBytes) setLoad({ phase: "compile", title, pct: 100 });
        },
      });
      loaded.current = key;
      return client;
    },
    [absorb],
  );

  const ensureReady = useCallback(async () => {
    try {
      await loadInto(llm, loadedKey, modelKey, modelKeyB ? " (A)" : "");
      if (modelKeyB) {
        await loadInto(llmB, loadedKeyB, modelKeyB, " (B)");
      } else if (llmB.current) {
        // Dropped back to a shared interpreter. Free the second set of weights
        // rather than leaving them resident for a run that will not use them.
        llmB.current.dispose();
        llmB.current = null;
        loadedKeyB.current = null;
      }
      for (const key of [aKey, bKey]) {
        if (packs.current[key]) continue;
        const meta = packByKey(key);
        if (!meta) throw new Error(`unknown lattice ${key}`);
        setStatus(`loading ${meta.persona}`);
        setLoad({ phase: "fetch", title: meta.persona, pct: 0 });
        packs.current[key] = await loadPack(meta.url, {
          passphrase: meta.passphrase,
          onProgress: (loaded, total) =>
            setLoad({
              phase: "fetch",
              title: meta.persona,
              loaded,
              total,
              pct: total > 0 ? (loaded / total) * 100 : 0,
            }),
        });
        setLoad({ phase: "decode", title: meta.persona, pct: 100 });
      }
      if (!embedder.current) {
        embedder.current = new EmbedClient();
        const name = packs.current[aKey].manifest.embedder;
        setStatus("loading embedder");
        setLoad({ phase: "compile", title: name.split("/").pop() ?? "embedder", pct: 0 });
        await embedder.current.load(name, "webgpu");
      }
      setStatus("ready");
    } finally {
      // Always closes, including on a throw. A veil left up after a failure
      // hides the error message underneath it, which is the worst possible
      // moment to be showing a loading screen.
      setLoad({ phase: null, title: "", pct: 0 });
      refreshResources();
    }
  }, [loadInto, modelKey, modelKeyB, aKey, bKey, refreshResources]);

  /**
   * A speaker: its lattice, its interpreter, and its voice.
   *
   * The client is set EXPLICITLY on both sides rather than left undefined on
   * A so it inherits the shared one. Same reasoning as `clientFor` in debate.ts:
   * an implicit fallback is fine until someone reads a log and has to work out
   * which model produced a card, and by then the record is already stamped.
   */
  const speakerFor = (key: string, client: MindClient | null): Speaker => {
    const pack = packs.current[key];
    const v = voices[key] ?? BLANK;
    return {
      key,
      // Empty means revert to the pack, not "speak as nobody".
      persona: v.persona.trim() || pack.manifest.persona,
      pack,
      llm: client ?? undefined,
      preamble: v.preamble.trim() ? v.preamble : undefined,
    };
  };

  const speakers = (): [Speaker, Speaker] => [
    speakerFor(aKey, llm.current),
    speakerFor(bKey, llmB.current ?? llm.current),
  ];

  const runAgenda = async () => {
    setError(null);
    setBusy(true);
    setAgenda(null);
    setPicked([]);
    setRounds([]);
    try {
      await ensureReady();
      const [a, b] = speakers();
      setStatus("agenda round");
      const res = await proposeAgenda(llm.current!, embedder.current!, a, b, topic, {
        config: cfg,
        mode,
      });
      setAgenda(res);
      setStatus("agenda ready, pick 1 to 3 questions");
    } catch (e) {
      setError(String((e as Error)?.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  const runDebate = async () => {
    if (picked.length === 0) return;
    setError(null);
    setBusy(true);
    setRounds([]);
    stop.current.stopped = false;
    try {
      await ensureReady();
      const [a, b] = speakers();
      setStatus("debating");
      const out = await runStagedDebate(llm.current!, embedder.current!, a, b, topic, picked, {
        config: cfg,
        mode,
        maxExchanges,
        stagingRetrieval,
        control: stop.current,
        onStatement: () => setRounds((r) => [...r]),
      });
      setRounds(out);
      setStatus("done");
    } catch (e) {
      setError(String((e as Error)?.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (set: Set<number>, id: number, fn: (s: Set<number>) => void) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    fn(next);
  };

  const edited = useMemo(() => changedIds(overrides), [overrides]);
  // Only the prompts in play for this mode. An operator tuning a collaboration
  // should not be scrolling past five prompts that never fire.
  const specs = useMemo(() => specsForMode(mode), [mode]);
  const verb = mode === "debate" ? "debate" : "collaborate on";

  const pct = usage && usage.quotaBytes > 0 ? (usage.usageBytes / usage.quotaBytes) * 100 : 0;
  /**
   * What the model files actually add up to, from verified manifests.
   *
   * Shown NEXT TO the browser's own figure rather than instead of it, because
   * the two disagree: 4.87 GB of models whose chunks were each confirmed
   * present, against 2.33 GB reported by storage.estimate(). That estimate is
   * deliberately approximate, browsers fuzz it to resist fingerprinting, so
   * neither number is simply wrong and picking one would hide the fact that
   * they diverge by a factor of two.
   */
  const modelBytes = Object.values(repos).reduce((n, r) => n + r.bytes, 0);
  /** What the two chosen models weigh together, for the warning next to the picker. */
  const twoModelBytes =
    (SHIPPED_MODELS.find((m) => m.key === modelKey)?.sizeBytes ?? 0) +
    (SHIPPED_MODELS.find((m) => m.key === modelKeyB)?.sizeBytes ?? 0);

  return (
    <div className="lab">
      <LoadingOverlay state={load} />
      <header className="lab-top">
        <h2>Pipeline</h2>
        <span className="lab-status">{status}</span>
        {busy && (
          <button
            className="ghost"
            onClick={() => {
              stop.current.stopped = true;
              setStatus("stopping after this turn");
            }}
          >
            stop
          </button>
        )}
        <button className="ghost" onClick={() => setPanelOpen((v) => !v)}>
          prompts{edited.length > 0 ? ` (${edited.length} edited)` : ""}
        </button>
        <button className="ghost" onClick={() => setLog([])}>
          clear log
        </button>
        {onExit && (
          <button className="ghost" onClick={onExit}>
            back
          </button>
        )}
      </header>

      {error && <div className="lab-error">{error}</div>}

      <div className="lab-body">
        <aside className="lab-setup">
          <h3>Setup</h3>
          <label>
            mode
            <select value={mode} disabled={busy} onChange={(e) => setMode(e.target.value as DebateMode)}>
              <option value="debate">debate: argue it out</option>
              <option value="collaborate">collaborate: build on each other</option>
            </select>
          </label>
          {/* No longer disabled once a model is loaded. It used to be, so
              changing weights meant reloading the page; `loadInto` swaps the
              client instead, freeing the old worker with the old weights. */}
          <label>
            model{modelKeyB ? " A" : ""}
            <select value={modelKey} disabled={busy} onChange={(e) => setModelKey(e.target.value)}>
              {SHIPPED_MODELS.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label} ({(m.sizeBytes / 1e6).toFixed(0)} MB)
                </option>
              ))}
            </select>
          </label>
          {/* TWO INTERPRETERS. Default is shared, which is the bay's thesis;
              this is how the thesis becomes falsifiable rather than true by
              construction. Costs a second download and a second shader
              compilation, so the total is shown next to it. */}
          <label>
            model B
            <select value={modelKeyB} disabled={busy} onChange={(e) => setModelKeyB(e.target.value)}>
              <option value={SAME_AS_A}>same weights as A (shared)</option>
              {SHIPPED_MODELS.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label} ({(m.sizeBytes / 1e6).toFixed(0)} MB)
                </option>
              ))}
            </select>
          </label>
          {modelKeyB && (
            <p className="lab-note">
              two models resident, {(twoModelBytes / 1e6).toFixed(0)} MB of weights. Every
              difference between the sides is now the lattice OR the model, so change one at
              a time.
            </p>
          )}
          <label>
            side A
            <select value={aKey} onChange={(e) => setAKey(e.target.value)}>
              {PACKS.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            side B
            <select value={bKey} onChange={(e) => setBKey(e.target.value)}>
              {PACKS.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          {/* THE PERSONA AND PREAMBLE, editable without re-exporting a pack.
              Free: "I would like to edit the preamble and the persona, I know
              they are in the lattice but I think being able to adjust those is
              important as much as the prompts."

              NOT in the prompt panel, and that is the whole reason this is a
              separate control. The prompt registry's active set is global, so a
              preamble edited there would apply to both speakers at once and
              collapse them into one voice, which is precisely the failure the
              `{preamble}` wrapper was designed to prevent. Per-speaker text
              needs a per-speaker box. */}
          {([[aKey, "A"], [bKey, "B"]] as const).map(([key, side]) => (
            <VoiceEditor
              key={`${side}-${key}`}
              side={side}
              packKey={key}
              baseline={packs.current[key]?.manifest}
              voice={voices[key] ?? BLANK}
              disabled={busy}
              onChange={(v) => setVoices((prev) => ({ ...prev, [key]: v }))}
            />
          ))}

          <label className="wide">
            topic
            <textarea value={topic} rows={3} onChange={(e) => setTopic(e.target.value)} />
          </label>

          <div className="dials">
            <label>
              exchanges
              <input
                type="number"
                min={1}
                max={12}
                value={maxExchanges}
                onChange={(e) => setMaxExchanges(Number(e.target.value))}
              />
            </label>
            <label>
              k
              <input
                type="number"
                min={1}
                max={16}
                value={cfg.k}
                onChange={(e) => setCfg({ ...cfg, k: Number(e.target.value) })}
              />
            </label>
            {/* THE RELEVANCE FLOOR, measured 2026-07-27 and known to be too low.
                At the shipped 0.45 it keeps 100% of right answers and admits
                100% of questions the lattice cannot answer: it rejects nothing
                at all. The knee is 0.62. Left at the default and offered as a
                dial because that measurement is n=30 on one lattice. */}
            <label>
              floor
              <input
                type="number"
                min={0}
                max={0.95}
                step={0.01}
                value={cfg.floor ?? RELEVANCE_FLOOR}
                onChange={(e) => setCfg({ ...cfg, floor: Number(e.target.value) })}
              />
            </label>
            <label>
              max tokens
              <input
                type="number"
                min={64}
                max={1024}
                step={32}
                value={cfg.maxNewTokens}
                onChange={(e) => setCfg({ ...cfg, maxNewTokens: Number(e.target.value) })}
              />
            </label>
            {/* #48. `continue_final_message` turned out NOT to be the fix:
                in transformers.js it only omits the generation prompt, leaving
                the note closed and no turn open at all. This templates
                everything before the note and appends it after the marker the
                template emitted, so the turn is genuinely still open. Default
                off until the grounding effect is measured. */}
            <label className="check">
              <input
                type="checkbox"
                checked={cfg.openPrefill ?? false}
                onChange={(e) => setCfg({ ...cfg, openPrefill: e.target.checked })}
              />
              open prefill
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={stagingRetrieval}
                onChange={(e) => setStagingRetrieval(e.target.checked)}
              />
              staging retrieval
            </label>
          </div>

          <button className="go" disabled={busy} onClick={() => void runAgenda()}>
            1. run agenda
          </button>

          {agenda && (
            <div className="agenda">
              <h4>Questions</h4>
              {Object.entries(agenda.health).map(([k, h]) => (
                <div key={k} className={`health ${h.warning ? "warn" : "ok"}`}>
                  <b>{short(h.persona)}</b> {h.questions} proposed
                  {h.topScore !== null && <span className="score"> top {h.topScore.toFixed(3)}</span>}
                  {h.warning && <div className="why">{h.warning}</div>}
                  {/* Never let a parser rejection hide behind a low count again. */}
                  {h.dropped?.map((d, i) => (
                    <div key={i} className="dropped">
                      dropped ({d.why}): {d.text.slice(0, 90)}
                      {d.text.length > 90 ? "..." : ""}
                    </div>
                  ))}
                </div>
              ))}
              {agenda.questions.map((q) => (
                <label key={q.text} className="pick">
                  <input
                    type="checkbox"
                    checked={picked.includes(q.text)}
                    disabled={!picked.includes(q.text) && picked.length >= 3}
                    onChange={(e) =>
                      setPicked((p) =>
                        e.target.checked ? [...p, q.text] : p.filter((x) => x !== q.text),
                      )
                    }
                  />
                  <span>
                    {q.text}
                    <em> {short(q.fromPersona)}</em>
                  </span>
                </label>
              ))}
              <button className="go" disabled={busy || picked.length === 0} onClick={() => void runDebate()}>
                2. {verb} {picked.length} question{picked.length === 1 ? "" : "s"}
              </button>
            </div>
          )}

          <div className="res">
            <h4>On this device</h4>
            {usage && (
              <>
                <div className="bar"><i style={{ width: `${Math.min(100, pct).toFixed(1)}%` }} /></div>
                <div className="res-line">
                  <span>{formatBytes(usage.usageBytes)} used</span>
                  <span>{formatBytes(usage.freeBytes)} free of {formatBytes(usage.quotaBytes)}</span>
                </div>
                {/* Quota is derived from free disk and moves. Measured 5.96 to
                    12.99 GB on one machine in a single day, so it is reported
                    rather than treated as a constant. */}
                <div className="res-line">
                  <span>{formatBytes(modelBytes)} in cached repos</span>
                  <span>{Object.keys(repos).length} repo(s)</span>
                </div>
                <div className="res-note">
                  Quota tracks free disk and changes. The browser's "used" figure is
                  approximate and does not have to match the model total, which is
                  summed from verified cache manifests.
                </div>
              </>
            )}

            <div className="res-models">
              {SHIPPED_MODELS.map((m) => {
                const got = repos[m.hfId];
                return (
                  <div key={m.key} className={`res-model ${got ? "cached" : ""} ${m.key === modelKey ? "picked" : ""}`}>
                    <span className="dot" />
                    <span className="nm">{m.label}</span>
                    <span className="sz">
                      {got ? `cached ${formatBytes(got.bytes)}` : `${formatBytes(m.sizeBytes)} to fetch`}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Repos that are NOT in the picker: the embedder and the image
                model. Without them the total does not add up to the list above
                it, and a figure you cannot account for is one you should not
                trust. Measured: 2.42 GB of Janus and 331 MB of embeddinggemma
                were the whole of a 2.75 GB discrepancy. */}
            {Object.entries(repos)
              .filter(([id]) => !SHIPPED_MODELS.some((m) => m.hfId === id))
              .map(([id, r]) => (
                <div key={id} className="res-model cached other">
                  <span className="dot" />
                  <span className="nm">{id.split("/").pop()}</span>
                  <span className="sz">cached {formatBytes(r.bytes)}</span>
                </div>
              ))}

            <div className="res-line">
              <span>{Object.keys(packs.current).length} lattice(s) in memory</span>
              <button
                className="ghost tiny"
                disabled={busy}
                onClick={() => {
                  void clearWeightCache().then(refreshResources);
                }}
              >
                clear weights
              </button>
            </div>

            {gpu && (
              <div className="res-note">
                {gpu.supported
                  ? `${gpu.vendor ?? "gpu"} ${gpu.architecture ?? ""} · f16 ${gpu.f16 ? "yes" : "no"} · max buffer ${formatBytes(gpu.maxBufferBytes)}`
                  : `WebGPU unavailable: ${gpu.reason ?? "unknown"}`}
              </div>
            )}
          </div>

          {rounds.length > 0 && (
            <div className="outcome">
              <h4>Transcript</h4>
              {rounds.map((r) => (
                <div key={r.question} className="qround">
                  <div className="q">{r.question}</div>
                  {/* Did each turn add KNOWLEDGE or only words? Echo cannot say
                      that in collaborate mode, where agreement is the goal. */}
                  <div className="cov">
                    {r.coverage.union} rows total ·{" "}
                    {(r.coverage.contributingTurns * 100).toFixed(0)}% of turns brought new ones
                    <span className="steps">
                      {r.coverage.steps.map((st, i) => (
                        <b key={i} className={st.fresh > 0 ? "new" : "none"}>
                          {st.fresh > 0 ? `+${st.fresh}` : "0"}
                        </b>
                      ))}
                    </span>
                  </div>
                  {r.openings.map((s, i) => (
                    <div key={`o${i}`} className="say">
                      <b>{short(s.persona)}</b> opening
                      <p>{s.answer}</p>
                    </div>
                  ))}
                  {r.rebuttals.map((x, i) => (
                    <div key={`r${i}`} className="say">
                      <b>{short(x.persona)}</b> rebuttal
                      {x.yielded && <span className="score"> rests</span>}
                      <span className="score">
                        {" "}echo {x.echo.score.toFixed(3)} · para {x.echo.paraphrase.toFixed(3)}
                      </span>
                      <p>{x.text}</p>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </aside>

        <main className="lab-log" ref={logRef}>
          {/* Two lanes, one per agent, chronological down the page. Free reads
              this by pattern rather than by prose, so which side a turn came
              from and what KIND of turn it was have to be visible without
              reading a word of it. */}
          <div className="lanes-head">
            <span className="lane-a">{packs.current[aKey]?.manifest.persona ?? aKey}</span>
            <span className="lane-b">{packs.current[bKey]?.manifest.persona ?? bKey}</span>
          </div>
          <div className="lanes">
          {log.length === 0 && (
            <div className="hint">
              Every call to the model appears here: the exact series of messages it was given, then what
              it said. Run the agenda to start. Nothing is truncated, and the prompt under "exact" is
              decoded from the tokens the model actually received rather than rebuilt from the messages.
            </div>
          )}
          {log.map((r) => {
            const open = openIds.has(r.id);
            return (
              <div
                key={r.id}
                className={
                  `gen k-${r.kind ?? "system"} ` +
                  `lane-${r.speaker === aKey ? "a" : r.speaker === bKey ? "b" : "both"} ` +
                  `${r.done ? "done" : "live"} ${r.error ? "failed" : ""}`
                }
              >
                <div className="gen-head" onClick={() => toggle(openIds, r.id, setOpenIds)}>
                  <span className="seq">#{r.id}</span>
                  <span className="label">{r.label ?? "generation"}</span>
                  <span className={`kind k-${r.kind ?? "system"}`}>{r.kind ?? "harness"}</span>
                  {/* Only when the two sides run different weights. With a
                      shared interpreter this would be the same string on every
                      card, and a column that never varies is noise. */}
                  {modelKeyB && r.model && (
                    <span className="model">
                      {SHIPPED_MODELS.find((m) => m.key === r.model)?.label ?? r.model}
                    </span>
                  )}
                  <span className="counts">
                    {r.messages.length} msg
                    {r.stats && ` · ${r.stats.promptTokens} in · ${r.stats.newTokens} out`}
                    {r.ms !== undefined && ` · ${(r.ms / 1000).toFixed(1)}s`}
                    {!r.done && " · generating"}
                  </span>
                  <span className="chev">{open ? "hide" : "show"}</span>
                </div>

                {open && (
                  <div className="gen-body">
                    {/* ONE BOX PER CHUNK THE MODEL RECEIVED, parts identifiable
                        inside it. Free: "any chunk that was input to the model
                        should be visibly 1 chunk. I just want essentially all
                        chunks along the way identifiable."

                        Drawing the parts as separate boxes would show an
                        interior more organised than the one that exists: the
                        memory banner, the opponent's turn and the instruction
                        really are one user message by the time it is sent. */}
                    <div className="series">
                      {r.messages.map((m, i) => (
                        <div key={i} className={`msg ${m.role}`}>
                          <div className="role">
                            {m.role}
                            {m.parts && m.parts.length > 1 && (
                              <span className="merged">
                                {m.parts.length} parts, sent as one
                              </span>
                            )}
                          </div>
                          {m.parts && m.parts.length > 0 ? (
                            m.parts.map((p, j) => (
                              <div key={j} className={`part p-${p.kind}`}>
                                <span className="ptag">{p.kind}</span>
                                <pre>{p.content}</pre>
                              </div>
                            ))
                          ) : (
                            <pre>{m.content}</pre>
                          )}
                        </div>
                      ))}
                    </div>

                    {r.prompt && (
                      <div className="exact">
                        <button className="ghost tiny" onClick={() => toggle(showExact, r.id, setShowExact)}>
                          {showExact.has(r.id) ? "hide exact prompt" : "exact prompt, as tokenised"}
                        </button>
                        {showExact.has(r.id) && <pre className="raw">{r.prompt}</pre>}
                      </div>
                    )}
                  </div>
                )}

                <div className="gen-out">
                  {r.error ? (
                    <span className="err">{r.error}</span>
                  ) : r.text !== undefined ? (
                    <pre>{r.text}</pre>
                  ) : (
                    <span className="waiting">waiting for the model</span>
                  )}
                </div>
              </div>
            );
          })}
          </div>
        </main>

        {panelOpen && (
          <aside className="lab-prompts">
            <h3>
              Prompts
              <button
                className="ghost tiny"
                disabled={edited.length === 0}
                onClick={() => setOverrides({})}
              >
                reset all
              </button>
            </h3>
            <p className="hint">
              Edits apply to the next generation. No reload. Placeholders in braces are filled in at run
              time, and a required one that goes missing is flagged rather than silently producing a
              debate about nothing.
            </p>
            {specs.map((spec) => {
              const text = overrides[spec.id] ?? spec.text;
              const missing = missingVars(spec, text);
              const unknown = unknownVars(spec, text);
              const dirty = text !== spec.text;
              return (
                <details key={spec.id} className={`pspec ${dirty ? "dirty" : ""}`}>
                  <summary>
                    <span className="stage">{spec.stage}</span>
                    {spec.label}
                    {dirty && <span className="dot">edited</span>}
                  </summary>
                  <p className="why">{spec.help}</p>
                  {spec.vars.length > 0 && (
                    <p className="vars">
                      {spec.vars.map((v) => (
                        <code key={v} className={spec.required.includes(v) ? "req" : ""}>
                          {`{${v}}`}
                        </code>
                      ))}
                    </p>
                  )}
                  <textarea
                    value={text}
                    rows={Math.min(16, text.split("\n").length + 1)}
                    onChange={(e) =>
                      setOverrides((o) => ({ ...o, [spec.id]: e.target.value }))
                    }
                  />
                  {missing.length > 0 && (
                    <div className="bad">
                      missing required {missing.map((v) => `{${v}}`).join(" ")} — this stage will not get
                      that value at all
                    </div>
                  )}
                  {unknown.length > 0 && (
                    <div className="warnline">
                      never supplied here: {unknown.map((v) => `{${v}}`).join(" ")}
                    </div>
                  )}
                  {dirty && (
                    <button
                      className="ghost tiny"
                      onClick={() =>
                        setOverrides((o) => {
                          const next = { ...o };
                          delete next[spec.id as PromptId];
                          return next;
                        })
                      }
                    >
                      revert this one
                    </button>
                  )}
                </details>
              );
            })}
          </aside>
        )}
      </div>
    </div>
  );
}

/**
 * One speaker's identity: who it says it is, and the text that makes it so.
 *
 * WHY THE PACK'S TEXT IS THE PLACEHOLDER rather than the initial value. An
 * editor pre-filled with the baked-in preamble cannot distinguish "I am using
 * the pack" from "I typed out the pack's own words", so reverting becomes a
 * copy-paste job and a stray keystroke silently forks the identity. Empty means
 * the pack, always, and the pack's text is shown greyed behind it.
 *
 * The consequence, stated on the label: clearing the box reverts, it does not
 * give the speaker an empty system prompt. That would be a different and much
 * worse experiment than the one anyone clearing a field is asking for.
 */
function VoiceEditor({
  side,
  packKey,
  baseline,
  voice,
  disabled,
  onChange,
}: {
  side: "A" | "B";
  packKey: string;
  baseline?: LoadedPack["manifest"];
  voice: { persona: string; preamble: string };
  disabled: boolean;
  onChange: (v: { persona: string; preamble: string }) => void;
}) {
  const edited = voice.persona.trim().length > 0 || voice.preamble.trim().length > 0;
  return (
    <details className={`voice${edited ? " edited" : ""}`}>
      <summary>
        voice {side}: {voice.persona.trim() || baseline?.persona || packKey}
        {edited && <span className="tag">edited</span>}
      </summary>
      {!baseline && <p className="lab-note">Run once to load this lattice and see its own text.</p>}
      <label className="wide">
        persona name
        <input
          value={voice.persona}
          disabled={disabled}
          placeholder={baseline?.persona ?? "loaded from the lattice"}
          onChange={(e) => onChange({ ...voice, persona: e.target.value })}
        />
      </label>
      <label className="wide">
        preamble (empty = use the lattice's own)
        <textarea
          value={voice.preamble}
          rows={10}
          disabled={disabled}
          placeholder={baseline?.preamble ?? "loaded from the lattice"}
          onChange={(e) => onChange({ ...voice, preamble: e.target.value })}
        />
      </label>
      {/* THE HAZARD, stated where it can be acted on. Every pack preamble ends
          with the same block of answer rules, and one of them is the binding
          sentence: "Only state a name, number, value or quotation you actually
          know." Measured on four models, softening that cost 37 points of
          grounding on Gemma 3 270M and made Qwen 3.5 0.8B invent 14 claims of
          18. A replacement preamble drops it silently, and the result reads
          fluently, which is the worst way for a safety rule to disappear. */}
      {edited && voice.preamble.trim() && !/actually know/i.test(voice.preamble) && (
        <p className="lab-warn">
          This preamble does not carry the answer rules. The pack's own text ends with
          them, and one is the anti-fabrication binding. Copy the lattice's text in and
          edit it instead of replacing it, unless dropping that is the experiment.
        </p>
      )}
      <div className="voice-actions">
        {baseline && (
          <button
            className="ghost tiny"
            disabled={disabled}
            onClick={() => onChange({ persona: baseline.persona, preamble: baseline.preamble })}
          >
            copy the lattice's text in
          </button>
        )}
        {edited && (
          <button className="ghost tiny" disabled={disabled} onClick={() => onChange({ persona: "", preamble: "" })}>
            revert to the lattice
          </button>
        )}
      </div>
    </details>
  );
}
